'use client';

import React from 'react';
import clsx from 'clsx';

export interface InfoRowProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Label text (e.g., "Email", "Phone Number")
   */
  label: string;

  /**
   * Value to display - can be string, number, or React node
   */
  value: React.ReactNode;

  /**
   * If true, displays value as muted text when empty
   */
  showEmptyState?: boolean;

  /**
   * Text to display when value is empty (default: "Not provided")
   */
  emptyText?: string;

  /**
   * If true, renders in vertical layout (stacked)
   * If false, renders in horizontal layout (side-by-side)
   */
  vertical?: boolean;

  /**
   * Icon to display before the label
   */
  icon?: React.ReactNode;

  /**
   * Additional CSS classes for the container
   */
  className?: string;

  /**
   * If true, applies a bottom border for visual separation
   */
  bordered?: boolean;

  /**
   * If true, allows text to be copied on click
   */
  copyable?: boolean;
}

/**
 * InfoRow Component
 *
 * A reusable component for displaying label-value pairs in information sections.
 * Supports both horizontal and vertical layouts, empty states, and copy-to-clipboard.
 *
 * @example
 * ```tsx
 * // Horizontal layout (default)
 * <InfoRow label="Email" value="john@example.com" />
 *
 * // Vertical layout
 * <InfoRow label="Address" value="123 Main St, City" vertical />
 *
 * // With empty state
 * <InfoRow label="Phone" value={null} showEmptyState />
 *
 * // With icon and copy
 * <InfoRow
 *   label="Registration No"
 *   value="MH01AB1234"
 *   icon={<CarIcon />}
 *   copyable
 * />
 * ```
 */
export function InfoRow({
  label,
  value,
  showEmptyState = true,
  emptyText = 'Not provided',
  vertical = false,
  icon,
  className,
  bordered = false,
  copyable = false,
  ...rest
}: InfoRowProps) {
  const [copied, setCopied] = React.useState(false);

  // Determine if value is empty
  const isEmpty =
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'string' && value.trim() === '');

  // Handle copy to clipboard
  const handleCopy = async () => {
    if (isEmpty || !copyable) return;

    try {
      const textValue = typeof value === 'string' ? value : String(value);
      await navigator.clipboard.writeText(textValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Render value with empty state handling
  const renderValue = () => {
    if (isEmpty && showEmptyState) {
      return <span className="text-gray-400 italic">{emptyText}</span>;
    }

    if (copyable && !isEmpty) {
      return (
        <button
          onClick={handleCopy}
          className="group inline-flex items-center gap-1.5 text-gray-900 transition-colors hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          title="Click to copy"
        >
          <span>{value}</span>
          <svg
            className="h-4 w-4 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            {copied ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            )}
          </svg>
        </button>
      );
    }

    return <span className="text-gray-900">{value}</span>;
  };

  if (vertical) {
    return (
      <div
        className={clsx(
          'py-3',
          bordered && 'border-b border-gray-200 last:border-b-0',
          className
        )}
        {...rest}
      >
        {/* Label */}
        <div className="mb-1 flex items-center gap-2">
          {icon && (
            <span className="flex-shrink-0 text-gray-400" aria-hidden="true">
              {icon}
            </span>
          )}
          <dt className="text-sm font-medium text-gray-500">{label}</dt>
        </div>

        {/* Value */}
        <dd className="text-base">{renderValue()}</dd>
      </div>
    );
  }

  // Horizontal layout
  return (
    <div
      className={clsx(
        'grid grid-cols-3 gap-4 py-3',
        bordered && 'border-b border-gray-200 last:border-b-0',
        className
      )}
      {...rest}
    >
      {/* Label */}
      <dt className="flex items-center gap-2 text-sm font-medium text-gray-500">
        {icon && (
          <span className="flex-shrink-0 text-gray-400" aria-hidden="true">
            {icon}
          </span>
        )}
        {label}
      </dt>

      {/* Value */}
      <dd className="col-span-2 text-sm">{renderValue()}</dd>
    </div>
  );
}

export default InfoRow;
